"use client";

import { useEffect, useState, useMemo, useRef } from "react";

const TICK_MS = 90;
const CANDLE_CLOSE_TICKS = 22;
const MAX_CANDLES = 48;
const PRICE_MIN = 22;
const PRICE_MAX = 78;

/** Brighter than logo teals so candles read on #0d1412 + quant-grid. */
const BULL_COLOR = "#4fd1b5";
const BEAR_COLOR = "#f87171";

type Candle = {
  id: number;
  o: number;
  h: number;
  l: number;
  c: number;
};

/** Numeric LCG so the first paint is identical on server and client. */
function seededRandom(seed: number): () => number {
  let s = seed | 0;
  return () => {
    s = (Math.imul(1664525, s) + 1013904223) | 0;
    return (s >>> 0) / 4294967296;
  };
}

function initCandles(
  count: number,
  startId: number,
  rand: () => number
): Candle[] {
  const out: Candle[] = [];
  let c = 50 + (rand() - 0.5) * 12;
  let id = startId;
  for (let i = 0; i < count; i++) {
    const o = c;
    c += (rand() - 0.48) * 7;
    c = Math.max(PRICE_MIN, Math.min(PRICE_MAX, c));
    const bodyHigh = Math.max(o, c);
    const bodyLow = Math.min(o, c);
    const h = Math.min(PRICE_MAX + 2, bodyHigh + rand() * 5);
    const l = Math.max(PRICE_MIN - 2, bodyLow - rand() * 5);
    out.push({ id: id++, o, h, l, c });
  }
  return out;
}

/** SVG y from price (higher price = smaller y). */
function ySvg(price: number): number {
  return 100 - price;
}

/**
 * Evolving candlestick chart for the hero background.
 * Scrolls left as new candles form; last bar updates like live price action.
 */
export function SparklineChart() {
  const nextIdRef = useRef(MAX_CANDLES);
  const tickRef = useRef(0);
  // Seeded first frame — Math.random() here mismatches SSR and blocks hydration.
  const [candles, setCandles] = useState<Candle[]>(() =>
    initCandles(MAX_CANDLES, 0, seededRandom(0xa7abe7))
  );

  useEffect(() => {
    const id = window.setInterval(() => {
      setCandles((prev) => {
        if (prev.length === 0) return prev;
        const next = [...prev];
        const lastIdx = next.length - 1;
        const last = { ...next[lastIdx] };

        last.c += (Math.random() - 0.5) * 2.2;
        last.c = Math.max(PRICE_MIN, Math.min(PRICE_MAX, last.c));
        last.h = Math.max(last.h, last.c, last.o);
        last.l = Math.min(last.l, last.c, last.o);

        tickRef.current += 1;
        if (tickRef.current >= CANDLE_CLOSE_TICKS) {
          tickRef.current = 0;
          next[lastIdx] = last;
          const open = last.c;
          next.push({
            id: nextIdRef.current++,
            o: open,
            h: open,
            l: open,
            c: open,
          });
          if (next.length > MAX_CANDLES) next.shift();
        } else {
          next[lastIdx] = last;
        }
        return next;
      });
    }, TICK_MS);

    return () => window.clearInterval(id);
  }, []);

  const elements = useMemo(() => {
    const n = candles.length;
    if (n === 0) return null;

    const pad = 3;
    const span = 100 - 2 * pad;
    const slot = n <= 1 ? 0 : span / (n - 1);
    const bodyW = Math.max(0.35, Math.min(2.2, slot * 0.55));

    return candles.map((c, i) => {
      const cx = pad + i * slot;
      const isUp = c.c >= c.o;
      const color = isUp ? BULL_COLOR : BEAR_COLOR;
      const yHigh = ySvg(c.h);
      const yLow = ySvg(c.l);
      const yTop = ySvg(Math.max(c.o, c.c));
      const yBot = ySvg(Math.min(c.o, c.c));
      const bodyH = Math.max(0.15, yBot - yTop);

      return (
        <g key={c.id}>
          <line
            x1={cx}
            y1={yHigh}
            x2={cx}
            y2={yLow}
            stroke={color}
            strokeWidth={0.22}
            strokeLinecap="round"
            opacity={0.62}
          />
          <rect
            x={cx - bodyW / 2}
            y={yTop}
            width={bodyW}
            height={bodyH}
            fill={color}
            opacity={0.52}
          />
        </g>
      );
    });
  }, [candles]);

  return (
    <div className="absolute inset-0 flex items-center justify-center">
      <svg
        className="w-full h-full"
        preserveAspectRatio="none"
        viewBox="0 0 100 100"
      >
        {elements}
      </svg>
    </div>
  );
}
